import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Users, Briefcase, RefreshCw, Navigation, Clock, Mail, UserCog, Loader2, ChevronRight, MapPinOff } from 'lucide-react';
import { Button } from '../components/ui/button';

import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const userIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const jobIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to auto-fit bounds when markers change
function MapBoundsHandler({ users, jobs }) {
  const map = useMap();
  
  useEffect(() => {
    if (users.length > 0 || jobs.length > 0) {
      const bounds = [];
      users.forEach(user => {
        if (user.latitude && user.longitude) {
          bounds.push([user.latitude, user.longitude]);
        }
      });
      jobs.forEach(job => {
        if (job.latitude && job.longitude) {
          bounds.push([job.latitude, job.longitude]);
        }
      });
      
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      }
    }
  }, [users, jobs, map]);
  
  return null;
}

const Map = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mapData, setMapData] = useState({ users: [], jobs: [], users_without_location: [], total_users: 0, total_jobs: 0, total_users_without_location: 0, total_clocked_in: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [center, setCenter] = useState([51.0447, -114.0719]); // Default: Calgary

  // Quick-actions sheet state — opens when admin/supervisor taps a user marker
  const [actionUser, setActionUser] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [reassignJobId, setReassignJobId] = useState('');
  const [reassignToId, setReassignToId] = useState('');
  const [reassignSaving, setReassignSaving] = useState(false);

  const isManager = user?.role === 'admin' || user?.role === 'supervisor';

  useEffect(() => {
    if (token) {
      fetchMapData();
      // Auto-refresh every 30 seconds
      const interval = setInterval(fetchMapData, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  // Fetch the user's currently-active jobs (todo / in_progress / paused) when the sheet opens
  useEffect(() => {
    const loadJobs = async () => {
      if (!actionUser) return;
      setLoadingJobs(true);
      try {
        const res = await axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } });
        const filtered = (res.data || []).filter(j =>
          j.assigned_to === actionUser.id &&
          ['todo', 'pending', 'accepted', 'in_progress', 'paused'].includes(j.status)
        );
        setActiveJobs(filtered);
        if (filtered.length === 1) setReassignJobId(filtered[0].id);
      } catch {
        // Silent — sheet still works without the job list
      } finally {
        setLoadingJobs(false);
      }
    };
    loadJobs();
  }, [actionUser, token]);

  // Reset reassign sub-flow whenever the sheet closes
  useEffect(() => {
    if (!actionUser) {
      setReassignJobId('');
      setReassignToId('');
      setActiveJobs([]);
    }
  }, [actionUser]);

  const handleReassign = async () => {
    if (!reassignJobId || !reassignToId) {
      toast({ title: 'Pick a job and assignee', variant: 'destructive' });
      return;
    }
    setReassignSaving(true);
    try {
      await axios.put(
        `${API}/jobs/${reassignJobId}/reassign?new_assignee_id=${encodeURIComponent(reassignToId)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Job reassigned' });
      setActionUser(null);
      fetchMapData();
    } catch (err) {
      toast({
        title: 'Reassign failed',
        description: err.response?.data?.detail || err.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setReassignSaving(false);
    }
  };

  const fetchMapData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/map/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMapData(response.data);
      
      // Set center to first available location
      if (response.data.users.length > 0) {
        setCenter([response.data.users[0].latitude, response.data.users[0].longitude]);
      } else if (response.data.jobs.length > 0) {
        setCenter([response.data.jobs[0].latitude, response.data.jobs[0].longitude]);
      }
    } catch (error) {
      console.error('Failed to fetch map data:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to load map data',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-[#1A2433] text-red-800',
      supervisor: 'bg-[#1A2433] text-blue-800',
      user: 'bg-[#1A2433] text-green-800'
    };
    return colors[role] || colors.user;
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: 'bg-[#1A2433] text-blue-800',
      in_progress: 'bg-[#1A2433] text-yellow-800',
      completed: 'bg-[#1A2433] text-green-800'
    };
    return colors[status] || colors.todo;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-[#1A2433] pb-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E88E5] to-[#00C896] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-3 min-w-0">
              <Navigation className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold truncate">Live Map View</h1>
                <p className="text-xs sm:text-sm text-white/95 font-medium truncate">Track users and jobs in real-time</p>
              </div>
            </div>
            <Button
              onClick={fetchMapData}
              disabled={isLoading}
              variant="ghost"
              className="text-white hover:bg-white/20 px-3 sm:px-4 flex-shrink-0"
              data-testid="map-refresh-btn"
            >
              <RefreshCw className={`w-5 h-5 sm:mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <Card className="border-2 border-[#2D3748] bg-[#0F1923]">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-[#1E88E5] font-semibold mb-1">Active Users</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white" data-testid="map-active-users-count">
                    {mapData.total_users}
                    {mapData.total_users_without_location > 0 && (
                      <span className="text-base sm:text-lg font-medium text-slate-400 ml-1">/ {mapData.total_clocked_in}</span>
                    )}
                  </p>
                  <p className="text-xs text-blue-300/80 mt-1 hidden sm:block">
                    {mapData.total_users_without_location > 0
                      ? `${mapData.total_users_without_location} clocked in without GPS`
                      : 'Currently clocked in'}
                  </p>
                </div>
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#1E88E5] rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-[#2D3748] bg-[#0F1923]">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-[#00C896] font-semibold mb-1">Active Jobs</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white" data-testid="map-active-jobs-count">{mapData.total_jobs}</p>
                  <p className="text-xs text-emerald-300/80 mt-1 hidden sm:block">To do or in progress</p>
                </div>
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#00C896] rounded-full flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map Container */}
        <Card className="shadow-xl border-2" data-testid="map-container">
          <CardHeader className="bg-gradient-to-r from-[#1A2433] to-[#1A2433] border-b py-3 sm:py-4">
            <CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
              <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#1E88E5]" />
              <span>Location Map</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Responsive height: 60vh on mobile (sits comfortably above the bottom nav), 600px on desktop */}
            <div style={{ width: '100%' }} className="h-[60vh] md:h-[600px]">
              {mapData.users.length === 0 && mapData.jobs.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-500">
                  <MapPin className="w-16 h-16 mb-4 text-gray-300" />
                  <p className="text-lg font-semibold">No locations to display</p>
                  <p className="text-sm">Users must clock in with location enabled, and jobs must have location data</p>
                </div>
              ) : (
                <MapContainer
                  center={center}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  
                  <MapBoundsHandler users={mapData.users} jobs={mapData.jobs} />

                  {/* User Markers — clustered so overlapping pins at the same job site collapse into a single number badge */}
                  <MarkerClusterGroup chunkedLoading showCoverageOnHover={false} maxClusterRadius={45}>
                    {mapData.users.map((u) => (
                    <Marker
                      key={u.id}
                      position={[u.latitude, u.longitude]}
                      icon={userIcon}
                    >
                      <Popup>
                        <div className="p-2 min-w-[200px]">
                          <h3 className="font-bold text-lg text-gray-900">{u.name}</h3>
                          <p className="text-sm text-gray-600 mb-2">{u.email}</p>
                          <div className="space-y-1">
                            <Badge className={getRoleColor(u.role)}>
                              {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                            </Badge>
                            <p className="text-xs text-gray-600 mt-2">
                              <strong>Clocked in at:</strong> {u.clocked_in_at}
                            </p>
                            <p className="text-xs text-gray-600">
                              <strong>Fit for duty:</strong> {u.fit_for_duty ? 'Yes' : 'No'}
                            </p>
                          </div>
                          {isManager && u.id !== user?.id && (
                            <Button
                              size="sm"
                              onClick={() => setActionUser(u)}
                              className="w-full mt-3 bg-[#1E88E5] hover:bg-blue-700 text-white"
                              data-testid={`map-user-actions-btn-${u.id}`}
                            >
                              <UserCog className="w-4 h-4 mr-1.5" />
                              Quick actions
                              <ChevronRight className="w-4 h-4 ml-auto" />
                            </Button>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  </MarkerClusterGroup>

                  {/* Job Markers — also clustered to keep busy sites readable */}
                  <MarkerClusterGroup chunkedLoading showCoverageOnHover={false} maxClusterRadius={45}>
                    {mapData.jobs.map((job) => (
                    <Marker
                      key={job.id}
                      position={[job.latitude, job.longitude]}
                      icon={jobIcon}
                    >
                      <Popup>
                        <div className="p-2">
                          <h3 className="font-bold text-lg text-gray-900">{job.title}</h3>
                          {job.description && (
                            <p className="text-sm text-gray-600 mb-2">{job.description}</p>
                          )}
                          <div className="space-y-1">
                            <Badge className={getStatusColor(job.status)}>
                              {job.status.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <p className="text-xs text-gray-600 mt-2">
                              <strong>Assigned to:</strong> {job.assigned_to}
                            </p>
                            {job.customer_name && (
                              <p className="text-xs text-gray-600">
                                <strong>Customer:</strong> {job.customer_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  </MarkerClusterGroup>
                </MapContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="mt-4 shadow-lg" data-testid="map-legend">
          <CardContent className="p-4">
            <div className="flex items-center justify-center gap-x-8 gap-y-2 flex-wrap">
              <div className="flex items-center gap-2" data-testid="map-legend-users">
                {/* Blue pin = active users (matches userIcon at top of file) */}
                <span
                  className="inline-block w-4 h-4 rounded-full ring-2 ring-white shadow"
                  style={{ background: '#2A81CB' }}
                  aria-hidden="true"
                />
                <span className="text-sm font-medium" style={{ color: '#1A2433' }}>Active Users</span>
              </div>
              <div className="flex items-center gap-2" data-testid="map-legend-jobs">
                {/* Green pin = job locations (matches jobIcon at top of file) */}
                <span
                  className="inline-block w-4 h-4 rounded-full ring-2 ring-white shadow"
                  style={{ background: '#2AAD27' }}
                  aria-hidden="true"
                />
                <span className="text-sm font-medium" style={{ color: '#1A2433' }}>Job Locations</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clocked in WITHOUT GPS — separate roster so admins can see who's missing from the map */}
        {mapData.users_without_location && mapData.users_without_location.length > 0 && (
          <Card className="mt-4 border-2 border-amber-700/60 bg-[#1A1410]" data-testid="map-no-gps-roster">
            <CardHeader className="py-3 sm:py-4 border-b border-amber-800/40">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-amber-200">
                <MapPinOff className="w-5 h-5 text-amber-400" />
                <span>Clocked in without location</span>
                <Badge className="bg-amber-600 text-white ml-1" data-testid="map-no-gps-count">
                  {mapData.users_without_location.length}
                </Badge>
              </CardTitle>
              <p className="text-xs text-amber-300/70 mt-1">
                These workers are on the clock but their device didn't share GPS — they won't appear on the map.
                Ask them to enable location permission in their browser/phone.
              </p>
            </CardHeader>
            <CardContent className="p-3 sm:p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="map-no-gps-list">
                {mapData.users_without_location.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-[#0F1923] border border-[#2D3748]"
                    data-testid={`map-no-gps-row-${u.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-amber-700/40 text-amber-200 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                      {u.name?.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-white truncate">{u.name}</div>
                      <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        Since {u.clocked_in_at}
                        <span className="text-slate-600">·</span>
                        <span className="capitalize">{u.role}</span>
                      </div>
                    </div>
                    <a
                      href={`mailto:${u.email}?subject=HinesTime%20%E2%80%94%20Please%20enable%20location`}
                      className="text-amber-300 hover:text-amber-200 p-1"
                      title={`Email ${u.name}`}
                      data-testid={`map-no-gps-email-${u.id}`}
                    >
                      <Mail className="w-4 h-4" />
                    </a>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick-Actions Sheet (mobile-first bottom sheet, also works on desktop) */}
      <Sheet open={!!actionUser} onOpenChange={(o) => { if (!o) setActionUser(null); }}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl max-h-[90vh] overflow-y-auto bg-[#0F1923] border-[#2D3748] text-white"
          data-testid="map-user-actions-sheet"
        >
          {actionUser && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-3 text-white">
                  <div className="w-10 h-10 rounded-full bg-[#1E88E5] flex items-center justify-center text-white font-bold">
                    {actionUser.name?.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <div className="text-base sm:text-lg font-semibold truncate">{actionUser.name}</div>
                    <div className="text-xs text-slate-400 font-normal truncate">{actionUser.email}</div>
                  </div>
                </SheetTitle>
                <SheetDescription className="text-slate-400 text-xs flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  Clocked in at {actionUser.clocked_in_at || '—'} · Fit for duty: {actionUser.fit_for_duty ? 'Yes' : 'No'}
                </SheetDescription>
              </SheetHeader>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4" data-testid="map-actions-grid">
                <Button
                  variant="outline"
                  className="justify-start h-12 border-[#2D3748] bg-[#1A2433] text-white hover:bg-[#2D3748]"
                  onClick={() => {
                    setActionUser(null);
                    navigate(`/manage-users?focus=${actionUser.id}`);
                  }}
                  data-testid="map-action-view-timesheet"
                >
                  <Clock className="w-4 h-4 mr-2 text-blue-400" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">Timesheet</div>
                    <div className="text-[10px] text-slate-400">View hours & shifts</div>
                  </div>
                </Button>

                <a
                  href={`mailto:${actionUser.email}?subject=HinesTime%20%E2%80%94%20Quick%20check-in`}
                  onClick={() => setActionUser(null)}
                  className="inline-flex items-center justify-start h-12 px-3 rounded-md border border-[#2D3748] bg-[#1A2433] text-white hover:bg-[#2D3748] text-sm"
                  data-testid="map-action-message"
                >
                  <Mail className="w-4 h-4 mr-2 text-emerald-400" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">Message</div>
                    <div className="text-[10px] text-slate-400">Email this worker</div>
                  </div>
                </a>

                <Button
                  variant="outline"
                  className="justify-start h-12 border-[#2D3748] bg-[#1A2433] text-white hover:bg-[#2D3748]"
                  onClick={() => {
                    // Just scroll the reassign block into view — doesn't close the sheet
                    document.getElementById('map-reassign-block')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  }}
                  data-testid="map-action-reassign-jump"
                  disabled={loadingJobs}
                >
                  <UserCog className="w-4 h-4 mr-2 text-amber-400" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">Reassign job</div>
                    <div className="text-[10px] text-slate-400">
                      {loadingJobs ? 'Loading…' : `${activeJobs.length} active`}
                    </div>
                  </div>
                </Button>
              </div>

              {/* Reassign sub-flow */}
              <div id="map-reassign-block" className="mt-5 rounded-lg border border-[#2D3748] bg-[#0B131F] p-4" data-testid="map-reassign-block">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3 flex items-center gap-2">
                  <UserCog className="w-3.5 h-3.5" />
                  Reassign one of {actionUser.name?.split(' ')[0] || 'this worker'}'s active jobs
                </div>

                {loadingJobs ? (
                  <div className="text-sm text-slate-400 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading active jobs…
                  </div>
                ) : activeJobs.length === 0 ? (
                  <div className="text-sm text-slate-400 italic">No active jobs assigned to {actionUser.name?.split(' ')[0] || 'this worker'}.</div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Job to reassign</label>
                      <Select value={reassignJobId} onValueChange={setReassignJobId}>
                        <SelectTrigger className="bg-[#1A2433] border-[#2D3748] text-white" data-testid="map-reassign-job-select">
                          <SelectValue placeholder="Pick a job" />
                        </SelectTrigger>
                        <SelectContent>
                          {activeJobs.map(j => (
                            <SelectItem key={j.id} value={j.id}>
                              {j.job_number ? `${j.job_number} · ` : ''}{j.title} ({j.status})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Reassign to</label>
                      <Select value={reassignToId} onValueChange={setReassignToId}>
                        <SelectTrigger className="bg-[#1A2433] border-[#2D3748] text-white" data-testid="map-reassign-to-select">
                          <SelectValue placeholder="Pick a worker" />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Use the live-on-map workers as a quick pick list */}
                          {mapData.users.filter(u => u.id !== actionUser.id).map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} <span className="text-xs text-slate-400 ml-1">· on map</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {mapData.users.filter(u => u.id !== actionUser.id).length === 0 && (
                        <p className="text-xs text-amber-400 mt-1">
                          No other workers are currently clocked in on the map. Use Manage Users for full reassignment.
                        </p>
                      )}
                    </div>

                    <Button
                      onClick={handleReassign}
                      disabled={!reassignJobId || !reassignToId || reassignSaving}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="map-reassign-confirm-btn"
                    >
                      {reassignSaving
                        ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reassigning…</>)
                        : (<><UserCog className="w-4 h-4 mr-2" />Reassign job</>)}
                    </Button>
                  </div>
                )}
              </div>

              <SheetFooter className="mt-4">
                <Button variant="ghost" onClick={() => setActionUser(null)} data-testid="map-actions-close">
                  Close
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>


    </div>
  );
};

export default Map;
